import type { ReactNode } from "react";

import { Footer } from "@/components/Footer";
import { HubAccount } from "@/components/HubAccount";
import { Masthead } from "@/components/Masthead";
import { PlayerDirectory } from "@/components/PlayerDirectory";
import { PROJECT_COUNT, ProjectShelf } from "@/components/ProjectShelf";
import { Tetromino } from "@/components/Tetromino";
import { api, type Roster } from "@/lib/api";
import { playersApi, type PlayerState } from "@/lib/playersApi";
import { sectionById } from "@/lib/sections";
import { useAccount } from "@/lib/useAccount";
import { useApi } from "@/lib/useApi";

/** A titled band, so the hub's three parts read as three parts. */
function Band({
  title,
  blurb,
  children,
}: {
  readonly title: string;
  readonly blurb?: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="mt-14 md:mt-20">
      <div className="mb-5 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h2 className="text-[clamp(1.5rem,3.5vw,2rem)]">{title}</h2>
        {blurb ? <span className="meta opacity-55">{blurb}</span> : null}
      </div>
      <div className="mb-6 h-0.5 w-full bg-ink/25" />
      {children}
    </section>
  );
}

export function Projects() {
  const club = useApi((signal) => api.club(signal));
  const section = sectionById("projects")!;
  const { session, loading } = useAccount();

  // The roster doubles as the profile directory: `has_profile` is exactly the
  // question the directory asks, and this response is already cached.
  const roster = useApi<Roster>((signal) => api.roster(signal));

  const player = useApi<PlayerState | null>(
    (signal) =>
      loading || !session ? Promise.resolve(null) : playersApi.mine(signal),
    [session?.user_id ?? null, loading],
  );

  return (
    <>
      <Masthead variant="page" club={club.data} />

      <main className="lattice min-h-[calc(100vh-64px)] border-b-2 border-ink">
        <div className="shell py-10 md:py-14">
          <header>
            <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
              <span
                className="flex h-16 w-16 shrink-0 items-center justify-center border-2 border-ink"
                style={{
                  background: `var(--color-piece-${section.piece.toLowerCase()})`,
                }}
              >
                <Tetromino
                  type={section.piece}
                  cell={11}
                  title={`${section.piece} piece`}
                />
              </span>

              <div>
                <span className="meta block opacity-65">
                  {section.index} — {section.piece} piece
                </span>
                <h1 className="text-[clamp(2.5rem,8vw,5rem)] leading-[0.9]">
                  {section.label}
                </h1>
              </div>

              <span className="meta ml-auto hidden text-right leading-relaxed opacity-60 lg:block">
                <span className="block font-mono text-2xl leading-none opacity-100">
                  {String(PROJECT_COUNT).padStart(2, "0")}
                </span>
                in the bag
              </span>
            </div>

            <div className="mt-6 h-1 w-full bg-ink" />

            <p className="mt-6 max-w-[58ch] leading-relaxed opacity-80">
              Everything the club has built, and the account that ties it
              together. Software written by members of Tetris At UCI — open to
              anyone in the club, whether you want to use it or help build it.
            </p>
          </header>

          <Band title="Minigames &amp; projects" blurb="Built by the club.">
            <ProjectShelf />
          </Band>

          <Band title="Your account" blurb="One sign-in for everything above.">
            <HubAccount player={player.data ?? null} error={player.error} />
          </Band>

          <Band title="Player profiles" blurb="Members who have written one.">
            {roster.loading ? (
              <p className="meta opacity-60">Loading…</p>
            ) : roster.error ? (
              // Without this an unreachable API is indistinguishable from a club
              // where nobody has written a profile yet.
              <p className="panel max-w-[52ch] px-5 py-4 leading-relaxed">
                {roster.error.message}
              </p>
            ) : (
              <PlayerDirectory players={roster.data?.players ?? []} />
            )}
          </Band>

          <aside className="mt-14 md:mt-20">
            <div className="slab max-w-[58ch] px-6 py-5">
              <p className="leading-relaxed">
                Working on something you would like listed here?{" "}
                <span className="font-bold">Bring it to a meeting.</span>
              </p>
            </div>
          </aside>
        </div>
      </main>

      <Footer club={club.data} />
    </>
  );
}
